import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectAllAccounts, selectGuestAccounts, useGetAccountsQuery, useGetGuestAccountsQuery } from "fond/api";
import { Account, GuestAccount } from "fond/types";

export type SwitchAccount = (Account | GuestAccount) & { Guest: boolean };

export interface UseSwitchableAccountsResult {
  isLoading: boolean;
  accounts: SwitchAccount[];
}

/**
 * Gets all accounts available for switching, including both user member accounts and guest accounts
 * @returns Object with isLoading state and array of account objects with Guest flag
 * - Member accounts: Full Account objects with all fields
 * - Guest accounts: GuestAccount objects with ID, Name, Logo, ForegroundColor, BackgroundColor
 */
export const useSwitchableAccounts = (): UseSwitchableAccountsResult => {
  const { isLoading: isLoadingAccounts } = useGetAccountsQuery();
  const { isLoading: isLoadingGuestAccounts } = useGetGuestAccountsQuery();
  const userAccounts = useSelector(selectAllAccounts);
  const guestAccounts = useSelector(selectGuestAccounts);

  const isLoading = isLoadingAccounts || isLoadingGuestAccounts;

  const accounts = useMemo(() => {
    const accountsMap = new Map<string, SwitchAccount>();
    const userAccountIds = new Set(userAccounts.map((account) => account.ID));

    // Add all user member accounts (full Account objects with all fields)
    userAccounts.forEach((account) => {
      accountsMap.set(account.ID, {
        ...account,
        Guest: false,
      });
    });

    // Add guest accounts from items where user is not a member (GuestAccount with visual branding fields)
    guestAccounts.forEach((account) => {
      if (!userAccountIds.has(account.ID)) {
        accountsMap.set(account.ID, {
          ...account,
          Guest: true,
        });
      }
    });

    return Array.from(accountsMap.values());
  }, [userAccounts, guestAccounts]);

  return { isLoading, accounts };
};
